import React, { FC, useMemo, useState } from "react";

import { CompleteDrillSessionKpiDialog } from "./CompleteDrillSessionKpiDialog";
import { Translate } from "@/app/application/Translate";
import {
	SessionKpi,
	TrainingDrillSessionStatus,
} from "@/features/training/model/Training/TrainingDrillSession";
import { EditDrillSession } from "@/features/training/pages/drill-tab/panel-right/drill-session/EditDrillSession";
import { CustomActionsButton } from "@/features/training/ui";
import { StyledMenu } from "@/shared/ui/StyledMenu";
import DoneIcon from "@mui/icons-material/Done";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import {
	Box,
	Divider,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Tooltip,
} from "@mui/material";

interface TrainingDrillSessionActionsProps {
	sessionId: number;
	status: TrainingDrillSessionStatus;
	kpiParameters: SessionKpi[];
}

export const TrainingDrillSessionActions: FC<
	TrainingDrillSessionActionsProps
> = ({ sessionId, status, kpiParameters }) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [editDrillSessionModal, setEditDrillSessionModal] = useState(false);
	const [kpiDialogOpen, setKpiDialogOpen] = useState(false);

	const open = Boolean(anchorEl);
	const isArchived = status === TrainingDrillSessionStatus.Archived;
	const isCompleted = status === TrainingDrillSessionStatus.Completed;

	const isFinal = isArchived || isCompleted;
	const canEdit = !isFinal;
	const canComplete = !isFinal;

	const handleClick = (e: React.MouseEvent<HTMLElement>) =>
		setAnchorEl(e.currentTarget);
	const handleClose = () => setAnchorEl(null);

	const openEdit = () => {
		setEditDrillSessionModal(true);
		handleClose();
	};

	const openCompleteDialog = () => {
		setKpiDialogOpen(true);
		handleClose();
	};

	// Compact menu styling
	const paperSx = useMemo(
		() => ({
			borderRadius: 3,
			mt: 1,
			minWidth: 240,
			overflow: "hidden",
			border: "1px solid",
			borderColor: "divider",
			boxShadow:
				"0px 14px 30px rgba(0,0,0,0.12), 0px 6px 12px rgba(0,0,0,0.08)",
		}),
		[],
	);

	const itemSx = useMemo(
		() => ({
			mx: 0.75,
			my: 0.25,
			borderRadius: 2,
			px: 1,
			py: 0.75,
			minHeight: 38,
			"& .MuiListItemIcon-root": {
				minWidth: 30,
				color: "text.primary", // monochrome
			},
			"& .MuiListItemText-primary": {
				fontSize: 14.5,
				fontWeight: 500,
				letterSpacing: 0.1,
			},
			"&:hover": { backgroundColor: "action.hover" },
			"&.Mui-disabled": { opacity: 0.55 },
		}),
		[],
	);

	// Optional: tooltip text when Complete is disabled (keeps menu compact)
	const completeDisabledTitle = isArchived
		? "Already archived"
		: isCompleted
			? "Already completed"
			: "";

	return (
		<>
			<Tooltip
				title={
					<Translate String="Training.DrillSession.Actions.Tooltip">
						Edit, complete, or archive this drill session
					</Translate>
				}
				enterDelay={600}
			>
				<Box display="inline-flex">
					<CustomActionsButton
						open={open}
						handleClick={handleClick}
						aria-haspopup="menu"
						aria-controls={open ? "drill-session-actions-menu" : undefined}
						aria-expanded={open ? "true" : undefined}
					>
						<Translate String="Training.Actions">Actions</Translate>
					</CustomActionsButton>
				</Box>
			</Tooltip>

			<StyledMenu
				id="drill-session-actions-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				keepMounted
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				PaperProps={{ sx: paperSx }}
				MenuListProps={{
					"aria-labelledby": "drill-session-actions-button",
					dense: false,
					sx: { py: 0.5 },
				}}
			>
				<MenuItem sx={itemSx} onClick={openEdit}>
					<ListItemIcon>
						<HistoryEduIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate
								HTMLTag="span"
								String={
									canEdit
										? "Training.DrillSession.Actions.Edit"
										: "Training.DrillSession.Actions.View"
								}
							>
								{canEdit ? "Edit session" : "View session"}
							</Translate>
						}
					/>
				</MenuItem>

				<Divider sx={{ my: 0.5, mx: 1.5 }} />

				<Tooltip
					title={completeDisabledTitle}
					disableHoverListener={canComplete}
					enterDelay={400}
					placement="right"
				>
					{/* span needed so Tooltip works on disabled MenuItem */}
					<span>
						<MenuItem
							sx={itemSx}
							onClick={openCompleteDialog}
							disabled={!canComplete}
						>
							<ListItemIcon>
								<DoneIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText
								primary={
									<Translate
										HTMLTag="span"
										String="Training.DrillSession.Actions.Complete"
									>
										Mark as complete
									</Translate>
								}
							/>
						</MenuItem>
					</span>
				</Tooltip>
			</StyledMenu>

			{/* Edit dialog */}
			<EditDrillSession
				open={editDrillSessionModal}
				handleClose={() => setEditDrillSessionModal(false)}
				id={sessionId}
				readOnly={isFinal}
			/>

			{/* KPI dialog */}
			<CompleteDrillSessionKpiDialog
				open={kpiDialogOpen}
				onClose={() => setKpiDialogOpen(false)}
				sessionId={sessionId}
				initialKpiParameters={kpiParameters}
			/>
		</>
	);
};
